import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

import { uniq } from "lodash";
import moment from "moment";
import { v4 as uuidv4 } from "uuid";

import { useCustomGoalsContext } from "@components/data/custom-goals-provider";
import { useUser } from "@components/user-context";
import { GET_OVERVIEW_METRICS_REPORT } from "@gql/overview-v2";
import {
  GET_UNIVERSAL_BENCHMARKS_DAYS,
  GET_UNIVERSAL_BENCHMARKS_METADATA,
} from "@gql/universal-bentchmarks";
import { useFeatureFlags, useNorthbeamQuery } from "@hooks/http";
import {
  makeUniversalBenchmarksBaselineDaysMetrics,
  makeUniversalBenchmarksKpiRows,
  type GetOverviewMetricsReport,
  type GetOverviewMetricsReportVariables,
  type GetUniversalBenchmarksDays,
  type GetUniversalBenchmarksDaysVariables,
  type GetUniversalBenchmarksMetadata,
} from "@north-beam/nb-client-side";
import {
  todayLocalAsISODate,
  type DateRangeChoiceV3,
  type FixedDateInterval,
  type IMCAttributionWindowDays,
  type INBAccountingMode,
} from "@north-beam/nb-common";
import { parseDateRangeArgument } from "@utils/index";

import { DEFAULT_BASELINE_FILTERS } from "./constants";
import { useAdoptableMetrics, useDaysSelection } from "./hooks";
import {
  type AdoptedMetrics,
  type AdoptedMetricsData,
  type BaselineFilters,
  type ConfiguratorContextDescriptor,
  type KpiMetricsData,
  type MetricPointWithDate,
  type StageStep,
  type WizardStep,
} from "./types";
import {
  adjustOverviewReportGrossRev,
  calculateBaselineDaysAverages,
  resolveBaseMetricsForGrossRev,
  roundNumberTwoDecimalPlaces,
} from "./utils";

export const ConfiguratorContext =
  createContext<ConfiguratorContextDescriptor | null>(null);

export const ConfiguratorProvider = ({ children }: { children: ReactNode }) => {
  const { user } = useUser();
  const { hasFeatureFlag } = useFeatureFlags();
  const { customGoals } = useCustomGoalsContext();

  const skipCogsCalculation = useRef(false);

  const [benchmarkAttributionModel, setBenchmarkAttributionModel] =
    useState<string>("northbeam_custom");
  const [benchmarkAccountingMode, setBenchmarkAccountingMode] =
    useState<INBAccountingMode>("cash");
  const [benchmarkAttributionWindow, setBenchmarkAttributionWindow] =
    useState<IMCAttributionWindowDays>("1");

  const [kpiAttributionModel, setKpiAttributionModel] =
    useState<string>("northbeam_custom");
  const [kpiAccountingMode, setKpiAccountingMode] =
    useState<INBAccountingMode>("accrual");
  const [kpiAttributionWindow, setKpiAttributionWindow] =
    useState<IMCAttributionWindowDays>("1");

  const [wizardStep, setWizardStep] = useState<WizardStep>(
    "select-baseline-days",
  );
  const [stage, setStage] = useState<StageStep>(
    hasFeatureFlag("enableUniversalBenchmarksStartPage") ? "intro" : "adopt",
  );

  const [adoptedMetrics, setAdoptedMetrics] = useState<AdoptedMetrics[]>([
    {
      id: uuidv4(),
      metricId: "roas",
      benchmarkFilters: [],
      kpiFilters: [],
    },
  ]);

  const [kpiMetricsData, setKpiMetricsData] = useState<KpiMetricsData[]>([]);
  const [adoptedBenchmarksData, setAdoptedBenchmarksData] = useState<
    AdoptedMetricsData[]
  >([]);
  const [adoptedKpiData, setAdoptedKpiData] = useState<AdoptedMetricsData[]>(
    [],
  );

  const [baselineFilters, setBaselineFilters] = useState<BaselineFilters>(
    DEFAULT_BASELINE_FILTERS,
  );

  const dateRangeInterval = useMemo(
    () =>
      parseDateRangeArgument(baselineFilters.dateRange, todayLocalAsISODate()),
    [baselineFilters.dateRange],
  );

  const { data: metadataData, loading: isMetadataLoading } =
    useNorthbeamQuery<GetUniversalBenchmarksMetadata>(
      GET_UNIVERSAL_BENCHMARKS_METADATA,
      {
        onCompleted(data) {
          const minDate = data.me.universalBenchmarksMetadata.minDate;
          const adoptedBenchmark =
            data.me.universalBenchmarksMetadata.adoptedBenchmark;

          let dateRange: FixedDateInterval | DateRangeChoiceV3 = "last12Months";
          const min = moment(minDate);
          const eligibleDays = moment().diff(min, "days");
          const yearAgo = moment().diff(
            moment().subtract(12, "months"),
            "days",
          );

          if (eligibleDays < yearAgo) {
            dateRange = {
              startDate: min.format("YYYY-MM-DD"),
              endDate: moment().subtract(1, "day").format("YYYY-MM-DD"),
              type: "fixed",
            };
          }

          if (baselineFilters.dateRange !== dateRange) {
            setBaselineFilters((prev) => ({ ...prev, dateRange }));
          }

          if (adoptedBenchmark) {
            const selectedMetrics = adoptedBenchmark.selectedMetricIds.filter(
              (e) => !(e.endsWith("Ft") || e.endsWith("Rtn")),
            );
            const baselineFilters = adoptedBenchmark.baselineFilters;
            const { startDate, endDate } = baselineFilters.dateRangeInterval;

            setAdoptedMetrics(
              selectedMetrics.map((metricId) => ({
                id: uuidv4(),
                metricId,
                benchmarkFilters: [],
                kpiFilters: [],
              })),
            );
            setBaselineFilters({
              ...baselineFilters,
              dateRange: {
                startDate,
                endDate,
                type: "fixed",
              },
            });
            if (
              baselineFilters.adjustment &&
              hasFeatureFlag("enableUniversalBenchmarksStartPage")
            ) {
              setStage("intro");
            } else {
              setStage("review");
              setWizardStep("adopt-benchmark");
            }
            skipCogsCalculation.current = true;
          }
        },
      },
    );

  const { data: baselineDaysData, loading: isBaselineDaysLoading } =
    useNorthbeamQuery<
      GetUniversalBenchmarksDays,
      GetUniversalBenchmarksDaysVariables
    >(GET_UNIVERSAL_BENCHMARKS_DAYS, {
      variables: {
        dateRange: dateRangeInterval,
        attributionModel: "northbeam_custom",
        accountingMode: "cash",
        attributionWindow: "infinity",
      },
      skip: isMetadataLoading,
      onCompleted(data) {
        if (skipCogsCalculation.current) {
          skipCogsCalculation.current = false;
          return;
        }

        const baselineDays = data.me.universalBenchmarksDays;
        const cancelledOrders = data.me.universalBenchmarksCancelledOrders;

        const {
          averageSpend,
          averageDiscount,
          averageShipping,
          averageTaxes,
          averageRefunds,
          averageCancellations,
        } = calculateBaselineDaysAverages({
          baselineDays,
          cancelledOrders,
          baselineMetrics,
          ecomAccountingSettings: user.ecomAccountingSettings,
          profitableDayThresholdMethod:
            baselineFilters.profitableDayThreshold.method,
        });

        setBaselineFilters((filters) => ({
          ...filters,
          cogs: {
            ...filters.cogs,
            spend: {
              ...filters.cogs.spend,
              value: roundNumberTwoDecimalPlaces(averageSpend),
            },
            discounts: {
              ...filters.cogs.discounts,
              value: roundNumberTwoDecimalPlaces(averageDiscount),
            },
            shipping: {
              ...filters.cogs.shipping,
              value: roundNumberTwoDecimalPlaces(averageShipping),
            },
            taxes: {
              ...filters.cogs.taxes,
              value: roundNumberTwoDecimalPlaces(averageTaxes),
            },
            returns: {
              ...filters.cogs.returns,
              value: roundNumberTwoDecimalPlaces(
                averageRefunds + averageCancellations,
              ),
            },
          },
          adjustment: null,
        }));
      },
    });

  const metadata = useMemo(() => {
    let minDate = null;
    let adoptedBenchmark = null;

    if (metadataData?.me.universalBenchmarksMetadata.minDate) {
      minDate = moment(
        metadataData?.me.universalBenchmarksMetadata.minDate,
      ).format("YYYY-MM-DD");
    }

    if (metadataData?.me.universalBenchmarksMetadata.adoptedBenchmark) {
      adoptedBenchmark =
        metadataData?.me.universalBenchmarksMetadata.adoptedBenchmark;
    }

    return { minDate, adoptedBenchmark };
  }, [metadataData]);

  const { baselineDays, cancelledOrders } = useMemo(() => {
    return {
      baselineDays: (baselineDaysData?.me.universalBenchmarksDays ??
        []) as MetricPointWithDate[],
      cancelledOrders: (baselineDaysData?.me
        .universalBenchmarksCancelledOrders ?? []) as MetricPointWithDate[],
    };
  }, [baselineDaysData]);

  const baselineMetrics = useMemo(() => {
    return makeUniversalBenchmarksBaselineDaysMetrics(
      "northbeam_custom",
      "infinity",
      "cash",
      user as any,
    );
  }, [user]);

  const buildKpiMetrics = useCallback(
    (
      attributionModel: string,
      attributionWindow: IMCAttributionWindowDays,
      accountingMode: INBAccountingMode,
    ) => {
      return makeUniversalBenchmarksKpiRows(
        attributionModel,
        accountingMode === "cash" ? "infinity" : attributionWindow,
        accountingMode,
        customGoals,
        user as any,
      ).map((metric) => {
        return {
          ...metric,
          id: metric.metadata?.overviewPageMetricId,
        };
      });
    },
    [customGoals, user],
  );

  const adoptableBenchmarksMetrics = useAdoptableMetrics(
    benchmarkAttributionModel,
    benchmarkAttributionWindow,
    benchmarkAccountingMode,
  );

  const adoptableKpiMetrics = useAdoptableMetrics(
    kpiAttributionModel,
    kpiAttributionWindow,
    kpiAccountingMode,
  );

  const loadMetricData = useCallback(
    (variables: GetOverviewMetricsReportVariables) => {
      return user.apolloClient.query<
        GetOverviewMetricsReport,
        GetOverviewMetricsReportVariables
      >({
        query: GET_OVERVIEW_METRICS_REPORT,
        variables,
      });
    },
    [user.apolloClient],
  );

  useEffect(() => {
    setKpiMetricsData(
      baselineFilters.kpiFilters.map((kpiFilter) => ({
        kpiMetricId: kpiFilter.id,
        status: "loading",
        data: null,
      })),
    );

    baselineFilters.kpiFilters.forEach(async (kpiFilter) => {
      const metrics = buildKpiMetrics(
        kpiFilter.attributionModel,
        kpiFilter.attributionWindow,
        kpiFilter.accountingMode,
      );
      const metric = metrics.find((e) => e.id === kpiFilter.metricId);

      if (!metric) {
        return;
      }

      const aw =
        kpiFilter.accountingMode === "cash"
          ? "infinity"
          : kpiFilter.attributionWindow;

      const variables: GetOverviewMetricsReportVariables = {
        attributionMethod: kpiFilter.attributionModel,
        accountingMode: kpiFilter.accountingMode,
        attributionWindow: aw,
        attributionWindows: [aw],
        timeGranularity: "daily",
        level: "campaign",
        dateRange: dateRangeInterval,
        metrics: resolveBaseMetricsForGrossRev(metric.requiredBaseMetrics),
        filters: kpiFilter.filters,
        disableDateCompression: true,
      };

      const { data } = await loadMetricData(variables);

      setKpiMetricsData((prev) =>
        prev.map((e) =>
          e.kpiMetricId === kpiFilter.id
            ? {
                ...e,
                status: "done",
                data: adjustOverviewReportGrossRev(
                  data,
                  user.ecomAccountingSettings,
                ),
              }
            : e,
        ),
      );
    });
  }, [
    baselineFilters.kpiFilters,
    dateRangeInterval,
    user.ecomAccountingSettings,
    loadMetricData,
    buildKpiMetrics,
  ]);

  useEffect(() => {
    setAdoptedBenchmarksData(
      adoptedMetrics.map((adoptedMetric) => ({
        metricId: adoptedMetric.id,
        status: "loading",
        data: null,
      })),
    );

    adoptedMetrics.forEach(async (adoptedMetric) => {
      const metric = adoptableBenchmarksMetrics.metrics.find(
        (e) => e.id === adoptedMetric.metricId,
      );

      if (!metric) {
        return;
      }

      const aw =
        benchmarkAccountingMode === "cash"
          ? "infinity"
          : benchmarkAttributionWindow;

      const variables: GetOverviewMetricsReportVariables = {
        attributionMethod: benchmarkAttributionModel,
        accountingMode: benchmarkAccountingMode,
        attributionWindow: aw,
        attributionWindows: [aw],
        timeGranularity: "daily",
        level: "campaign",
        dateRange: dateRangeInterval,
        metrics: uniq([
          ...resolveBaseMetricsForGrossRev(metric.requiredBaseMetrics),
          "rev",
          "spend",
          "txns",
        ]),
        filters: adoptedMetric.benchmarkFilters,
        disableDateCompression: true,
      };

      const { data } = await loadMetricData(variables);

      setAdoptedBenchmarksData((prev) =>
        prev.map((e) =>
          e.metricId === adoptedMetric.id
            ? {
                ...e,
                status: "done",
                data: data.me.overviewMetricsReport,
              }
            : e,
        ),
      );
    });
  }, [
    adoptedMetrics,
    adoptableBenchmarksMetrics,
    benchmarkAttributionModel,
    benchmarkAccountingMode,
    benchmarkAttributionWindow,
    dateRangeInterval,
    user.ecomAccountingSettings,
    loadMetricData,
  ]);

  useEffect(() => {
    setAdoptedKpiData(
      adoptedMetrics.map((adoptedMetric) => ({
        metricId: adoptedMetric.id,
        status: "loading",
        data: null,
      })),
    );

    adoptedMetrics.forEach(async (adoptedMetric) => {
      const metric = adoptableKpiMetrics.metrics.find(
        (e) => e.id === adoptedMetric.metricId,
      );

      if (!metric) {
        return;
      }

      const aw =
        kpiAccountingMode === "cash" ? "infinity" : kpiAttributionWindow;

      const variables: GetOverviewMetricsReportVariables = {
        attributionMethod: kpiAttributionModel,
        accountingMode: kpiAccountingMode,
        attributionWindow: aw,
        attributionWindows: [aw],
        timeGranularity: "daily",
        level: "campaign",
        dateRange: dateRangeInterval,
        metrics: uniq([
          "spend",
          "rev",
          ...resolveBaseMetricsForGrossRev(metric.requiredBaseMetrics),
        ]),
        breakdowns: ["Platform (Northbeam)"],
        filters: adoptedMetric.kpiFilters,
        disableDateCompression: true,
      };

      const { data } = await loadMetricData(variables);

      setAdoptedKpiData((prev) =>
        prev.map((e) =>
          e.metricId === adoptedMetric.id
            ? {
                ...e,
                status: "done",
                data: data.me.overviewMetricsReport,
              }
            : e,
        ),
      );
    });
  }, [
    adoptedMetrics,
    adoptableKpiMetrics,
    kpiAttributionModel,
    kpiAccountingMode,
    kpiAttributionWindow,
    dateRangeInterval,
    user.ecomAccountingSettings,
    loadMetricData,
  ]);

  const daysSelection = useDaysSelection({
    baselineMetrics,
    baselineDays,
    baselineFilters,
    wizardStep,
    kpiMetricsData,
    adoptableKpiMetrics,
    adoptedMetrics,
    adoptedKpiData,
    buildKpiMetrics,
  });

  return (
    <ConfiguratorContext.Provider
      value={{
        benchmarkAttributionModel,
        benchmarkAccountingMode,
        benchmarkAttributionWindow,
        kpiAttributionModel,
        kpiAccountingMode,
        kpiAttributionWindow,
        isMetadataLoading,
        isBaselineDaysLoading,
        baselineDays,
        cancelledOrders,
        baselineFilters,
        baselineMetrics,
        daysSelection,
        wizardStep,
        stage,
        adoptableBenchmarksMetrics,
        adoptableKpiMetrics,
        kpiMetricsData,
        adoptedMetrics,
        adoptedBenchmarksData,
        adoptedKpiData,
        metadata,
        buildKpiMetrics,
        setBenchmarkAttributionModel,
        setBenchmarkAccountingMode,
        setBenchmarkAttributionWindow,
        setKpiAttributionModel,
        setKpiAccountingMode,
        setKpiAttributionWindow,
        setBaselineFilters: useCallback((filters) => {
          setStage("adopt");
          setBaselineFilters(filters);
        }, []),
        setWizardStep,
        setStage,
        setAdoptedMetrics: useCallback((metrics) => {
          setStage("adopt");
          setAdoptedMetrics(metrics);
        }, []),
      }}
    >
      {children}
    </ConfiguratorContext.Provider>
  );
};
